import DayJsUtils from '@date-io/dayjs';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { Locale } from 'dayjs/locale/*';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import React from 'react';

import { IconButton, InputAdornment, TextField } from '@material-ui/core';
import CalendarIcon from '@material-ui/icons/DateRangeOutlined';
import { DatePicker as MUIDatePicker, DateTimePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';

import { useStyles } from './styles';

dayjs.extend(utc);
dayjs.extend(timezone);

type ComponentProps = {
	id?: string;
	value: dayjs.Dayjs | string;
	onChange: (date) => void;
	inputLabel?: string;
	time?: boolean;
	disablePast?: boolean;
	inputClassName?: string;
	cancelLabel?: string;
	minDate?: any;
	timezoneLabel?: string;
	locale?: Locale;
	displayElement?: React.ReactElement;
	open?: boolean;
	controlOpenState?: boolean;
	zIndex?: number;
	onClose?: () => void;
	nullable?: boolean;
};

const DatePicker = (props: ComponentProps) => {
	const {
		id,
		value,
		inputLabel = '',
		onChange,
		time,
		disablePast = false,
		cancelLabel,
		inputClassName,
		minDate,
		timezoneLabel,
		locale,
		displayElement,
		controlOpenState = false,
		open,
		zIndex = 1300,
		onClose,
		nullable
	} = props;

	const classes = useStyles();

	const onDateChange = React.useCallback(
		(date: dayjs.Dayjs) => {
			let formattedDate = date;
			if (timezoneLabel) {
				const noTimezoneDate = date.utc(true).format();
				formattedDate = dayjs.tz(noTimezoneDate, timezoneLabel);
			}
			onChange(formattedDate);
		},
		[onChange, timezoneLabel]
	);

	const textField = React.useCallback(
		props => {
			if (displayElement) return displayElement;
			return (
				<TextField
					{...props}
					size="small"
					className={clsx(classes.dateInput, inputClassName)}
					fullWidth
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<IconButton>
									<CalendarIcon />
								</IconButton>
							</InputAdornment>
						)
					}}
					label={nullable && !value ? null : inputLabel}
				/>
			);
		},
		[displayElement, classes.dateInput, inputClassName, nullable, value, inputLabel]
	);

	const datePicker = React.useMemo(() => {
		const commonProps = {
			id: id || 'dateInput',
			disablePast: disablePast,
			autoOk: true,
			cancelLabel: cancelLabel || 'Cancel',
			value: value ? value : nullable ? null : dayjs(),
			defaultVale: value ? value : nullable ? null : dayjs(),
			placeholder: inputLabel,
			clearable: nullable
		};
		if (controlOpenState) {
			commonProps['open'] = open;
			commonProps['onClose'] = onClose;
		}
		return time ? (
			<DateTimePicker
				{...commonProps}
				ampm={false}
				inputVariant="outlined"
				margin="normal"
				format="DD/MM/YYYY HH:mm"
				onChange={date => onDateChange(date)}
				TextFieldComponent={textFieldProps => textField(textFieldProps)}
				DialogProps={{
					style: {
						zIndex
					}
				}}
			/>
		) : (
			<MUIDatePicker
				{...commonProps}
				orientation="landscape"
				minDate={minDate || ''}
				inputVariant="outlined"
				margin="normal"
				format="DD/MM/YYYY"
				onChange={date => onDateChange(date)}
				TextFieldComponent={textFieldProps => textField(textFieldProps)}
				DialogProps={{
					style: {
						zIndex
					}
				}}
			/>
		);
	}, [id, disablePast, cancelLabel, value, nullable, inputLabel, controlOpenState, time, zIndex, minDate, open, onClose, onDateChange, textField]);

	return (
		<MuiPickersUtilsProvider utils={DayJsUtils} locale={locale || ''}>
			{datePicker}
		</MuiPickersUtilsProvider>
	);
};

export default React.memo(DatePicker);
